﻿// eslint-disable-next-line no-unused-vars
import Nav from "../components/nav/nav";
import Sidebar from "../components/sidebar";
import ContextMenu from "../components/contextMenu";
// eslint-disable-next-line no-unused-vars
import NavigationPanelTabs from "./NavigationPanelTabs";
import utils from "../core/utils";
import { contentTabId } from "./sidebarDataConverter";
import secondaryMenu from "./secondaryMenu";
import SvgManager from "../core/SvgManager";

const icons = {
  back: "../images/BackFull.svg",
  close: "../images/Close.svg",
  defaultItemType: "../images/DefaultItemType.svg",
  search: "../images/ExecuteSearch.svg",
  open: "../images/OpenInTab.svg",
  pinned: "../images/PinnedOn.svg",
  savedSearch: "../images/SavedSearchOverlay.svg",
  unpinned: "../images/PinnedOff.svg",
};

SvgManager.enqueue(Object.values(icons));

const disabledRowHighlightingClass =
  "aras-navigation-panel_row-highlighting-disabled";

const dashboardProperties = ["startPage", "on_click_handler", "formId"];
const isDashboard = (item) =>
  dashboardProperties.some((property) => item[property]) || !item.itemTypeId;

function navFormatter(nav, item) {
  const itemValue = item.value;
  if (itemValue.children) {
    return null;
  }

  const isDashboardItem = isDashboard(itemValue);
  if (itemValue.itemTypeId || isDashboardItem) {
    const leafTemplate = nav.templates.getDefaultTemplate(item);
    const nodeClassList = isDashboardItem
      ? "aras-nav-leaf-ico"
      : "aras-button aras-button_c aras-nav-leaf-ico";
    const nodeIconUrl = isDashboardItem ? icons.open : icons.search;

    leafTemplate.push(
      Inferno.createVNode(
        Inferno.getFlagsForElementVnode("span"),
        "span",
        nodeClassList,
        ArasModules.SvgManager.createInfernoVNode(nodeIconUrl),
        utils.infernoFlags.unknownChildren
      )
    );

    return leafTemplate;
  }

  return null;
}

export default class NavigationPanel extends Sidebar {
  nav = document.createElement("aras-nav");
  tabs = document.createElement("aras-navigation-panel-tabs");
  // Search bar elements
  _searchBar = null;
  _searchInput = null;
  _searchClear = null;
  _searchText = "";
  // Expansion state to restore when the search is cleared (DEF03191).
  _preSearchExpandedKeys = null;

  togglePin(pinned) {
    super.togglePin(pinned);
    this.render();
  }

  toggleVisibility(visible) {
    super.toggleVisibility(visible);
    const splitter = this.nextElementSibling;
    const isVisible = this.isVisible;
    splitter.classList.toggle("aras-hide", !isVisible);
    if (isVisible && this.isPinned) {
      setTimeout(() => {
        this.focus();
      });
    }
  }

  focus() {
    if (!this._secondaryMenuItemTypeId) {
      return super.focus();
    }

    const quickSearch = this.querySelector(
      ".aras-secondary-menu__quick-search:not([disabled])"
    );

    if (quickSearch) {
      quickSearch.focus();
    } else {
      const navHeader = this.querySelector(".aras-navigation-panel__header");
      navHeader.focus();
    }
  }

  render() {
    const secondaryMenuData = this._getSecondaryMenuData();
    const title = secondaryMenuData
      ? secondaryMenuData.pluralLabel
      : aras.getResource("", "common.contents");

    const pinIcon = this.isPinned ? icons.pinned : icons.unpinned;
    const pinIconNode = SvgManager.createHyperHTMLNode(pinIcon, {
      class: "aras-button__icon",
    });
    this.html`
		<div class="aras-navigation-panel__header" tabindex="0" aria-label="${title}">
			<button
				class="${"aras-button aras-button_c " + (secondaryMenuData ? "" : "aras-hide")}"
				onclick="${() => this._hideSecondaryMenu()}"
				title="${aras.getResource(
          "",
          "navigation_panel.secondary_menu.back_to_contents"
        )}"
			>
				${ArasModules.SvgManager.createHyperHTMLNode(icons.back, {
          class: "aras-button__icon",
        })}
			</button>
			${ArasModules.SvgManager.createHyperHTMLNode(
        secondaryMenuData && secondaryMenuData.icon,
        { class: "aras-navigation-panel__header-icon" }
      )}
			<span class="aras-navigation-panel__header-title">
				${title}
			</span>
			<span
				class="aras-navigation-panel__pin-icon aras-button aras-button_c"
				onclick="${() => this.togglePin()}"
			>
				${pinIconNode}
			</span>
		</div>
		${!secondaryMenuData ? this._searchBar : ""}
		${this.nav}
		${secondaryMenu(this, secondaryMenuData)}
		${this.tabs}`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.classList.add("aras-navigation-panel", disabledRowHighlightingClass);
    this.setAttribute("role", "navigation");
    this.popupMenu = new ContextMenu();
    this._initContextMenuHandlers();
    this._initContextMenuListeners();
    const observer = new MutationObserver((mutations) => {
      const navPanel = mutations[0].target;
      const panelWidth = navPanel.style.width;
      navPanel.nextElementSibling.style.left = panelWidth;
    });

    observer.observe(this, {
      attributes: true,
      attributeFilter: ["style"],
    });
    this.render();
  }

  created() {
    this._initTOC();
    this._initTabs();

    // If searchbar is created
    if (this._searchBar) {
      return;
    }
    this._createSearchBar();
  }

  _createSearchBar() {
    this._searchBar = document.createElement("div");
    this._searchBar.className = "aras-navigation-panel__search";
    this._searchBar.innerHTML = `
				<input
					type="text"
					class="aras-navigation-panel__search-input"
					aria-label="Search contents"
					placeholder="Search..."
				/>
				<button
					type="button"
					class="aras-navigation-panel__search-clear aras-button aras-button_c"
					title="Clear Search"
					aria-label="Clear Search"
				></button>
			`;

    this._searchInput = this._searchBar.querySelector(
      ".aras-navigation-panel__search-input"
    );
    this._searchClear = this._searchBar.querySelector(
      ".aras-navigation-panel__search-clear"
    );

    this._searchInput.addEventListener("input", (e) => {
      this._applySearchFilter(e.target.value);
    });

    this._searchInput.addEventListener("keydown", (e) => {
      if (e.code === "Escape" || e.key === "Escape") {
        this._clearSearch(true);
      }
    });

    this._searchClear.addEventListener("click", () => {
      this._clearSearch(true);
    });

    this._updateClearButtonState();
  }

  _updateClearButtonState() {
    if (!this._searchClear) {
      return;
    }
    this._searchClear.disabled = this._searchText === "";
  }

  // refocusInput is only passed when the user cleared the box themselves, so
  // that clearing from _showSecondaryMenu() does not steal focus from the
  // quick search input that has just been focused.
  _clearSearch(refocusInput) {
    const hasActiveFilter =
      this._searchText !== "" || Boolean(this.nav && this.nav.filteredItems);

    if (this._searchInput) {
      this._searchInput.value = "";
    }
    if (hasActiveFilter) {
      this._applySearchFilter("");
    }
    if (refocusInput && this._searchInput) {
      this._searchInput.focus();
    }
  }

  /**
   * DEF03191 - filter the TOC through the state aras-nav renders from
   * (filteredItems / expandedItemsKeys) rather than by hiding rows in the DOM.
   * The nav re-renders itself with Inferno on every click, so anything set by
   * hand on its nodes either gets wiped (className is rebuilt from
   * expandedItemsKeys, so a branch collapsed the moment it was clicked) or is
   * left behind (inline display:none survived the patch, so children stayed
   * invisible after expanding). Clearing the search also used to strip
   * aras-nav__parent_expanded without clearing expandedItemsKeys, which left
   * the state saying "expanded" while the DOM showed "collapsed" - the next
   * click on that category then hit collapse() instead of expand().
   */
  _applySearchFilter(value) {
    this._searchText = (value || "").trim();
    this._updateClearButtonState();

    const nav = this.nav;
    const items = nav && nav.dataStore && nav.dataStore.items;
    if (!items) {
      return;
    }

    if (this._searchText === "") {
      nav.filteredItems = null;
      if (this._preSearchExpandedKeys) {
        nav.expandedItemsKeys = this._preSearchExpandedKeys;
        this._preSearchExpandedKeys = null;
      }
      nav.render();
      return;
    }

    // Remember what the user had open so clearing the search restores it.
    if (!this._preSearchExpandedKeys) {
      this._preSearchExpandedKeys = new Set(nav.expandedItemsKeys);
    }

    const parentKeys = new Map();
    items.forEach((item, key) => {
      if (!item.children) {
        return;
      }
      item.children.forEach((childKey) => parentKeys.set(childKey, key));
    });

    const query = this._searchText.toLowerCase();
    const visibleKeys = new Set();
    const expandedKeys = new Set();
    const walkedKeys = new Set();

    // A matched category keeps its whole subtree so it can still be drilled
    // into, exactly as it can be without a search.
    const addWithDescendants = (key) => {
      if (walkedKeys.has(key)) {
        return;
      }
      walkedKeys.add(key);
      visibleKeys.add(key);
      const item = items.get(key);
      if (item && item.children) {
        item.children.forEach(addWithDescendants);
      }
    };

    items.forEach((item, key) => {
      const label = item.label || "";
      if (label.toLowerCase().indexOf(query) === -1) {
        return;
      }
      addWithDescendants(key);
      // Keep every ancestor, and expand it, so the match is reachable.
      let parentKey = parentKeys.get(key);
      while (parentKey !== undefined && !expandedKeys.has(parentKey)) {
        visibleKeys.add(parentKey);
        expandedKeys.add(parentKey);
        parentKey = parentKeys.get(parentKey);
      }
    });

    nav.filteredItems = visibleKeys;
    nav.expandedItemsKeys = expandedKeys;
    nav.render();
  }

  _initContextMenuHandlers() {
    const sidebarTabs = this.tabs;
    const contextMenuMethods = {
      pinItemType: (id, tabData) => sidebarTabs.addTab(id, tabData),
      unpinItemType: (id) => {
        const tabsData = sidebarTabs.data;
        const removedTab = tabsData.get(id);
        sidebarTabs.removeTab(id);
        return removedTab;
      },
      getItemTypeByItemTypeId: (itemTypeId) => {
        let tabId;
        const tabsData = sidebarTabs.data;
        tabsData.forEach((data, id) => {
          if (data.itemTypeId === itemTypeId) {
            tabId = id;
          }
        });
        return tabsData.get(tabId);
      },
      isDashboard,
      openSearch: (...args) => window.arasTabs.openSearch(...args),
    };
    const dispatchContextMenuEvent = (location, event, options) => {
      event.preventDefault();
      const detail = {
        location,
        x: event.clientX,
        y: event.clientY,
        ...options,
      };
      const cuiEvent = new CustomEvent("navigationPanelContextMenu", {
        detail,
      });
      this.dispatchEvent(cuiEvent);
    };
    sidebarTabs.on("contextmenu", (itemKey, event) => {
      const currentTarget = sidebarTabs.data.get(itemKey);
      dispatchContextMenuEvent("PopupMenuMainWindowTOC", event, {
        currentTarget,
        ...contextMenuMethods,
      });
    });
    const nav = this.nav;
    nav.on("contextmenu", (itemKey, event) => {
      nav.select(itemKey);
      const currentTarget = nav.data.get(itemKey);
      dispatchContextMenuEvent("PopupMenuMainWindowTOC", event, {
        currentTarget,
        ...contextMenuMethods,
      });
    });
    this._favoritesContextMenuHandler = (event) => {
      const favoriteItemRow = event.target.closest(".aras-nav__child");
      if (!favoriteItemRow) {
        return;
      }

      favoriteItemRow.classList.add("aras-nav__child_selected");
      const unselectHighlightedItem = () => {
        favoriteItemRow.classList.remove("aras-nav__child_selected");
        this.popupMenu.dom.removeEventListener(
          "contextMenuClose",
          unselectHighlightedItem
        );
      };
      this.popupMenu.dom.addEventListener(
        "contextMenuClose",
        unselectHighlightedItem
      );

      const favoriteId = favoriteItemRow.dataset.id;
      const currentTarget = window.favorites.get(favoriteId);
      dispatchContextMenuEvent("PopupMenuSecondaryMenu", event, {
        currentTarget,
      });
    };
  }

  _initContextMenuListeners() {
    const contextMenuDom = this.popupMenu.dom;
    contextMenuDom.addEventListener("contextMenuShow", () => {
      this.classList.remove(disabledRowHighlightingClass);
    });
    contextMenuDom.addEventListener("contextMenuClose", (e) => {
      this.classList.add(disabledRowHighlightingClass);
      if (e.detail.type === "keydown") {
        this.focus();
        return;
      }

      if (!this.isPinned) {
        setTimeout(() => {
          this.closeIfNotAcitive(document.activeElement);
        });
      }
    });
    contextMenuDom.addEventListener("click", () => {
      this._hideIfNotPinned();
    });
  }

  _initTOC() {
 
    const selectTab = (dataItem) => {
      const tabs = this.tabs;
      tabs.data.forEach((tabItem, id) => {
        if (tabItem.itemTypeId === dataItem.itemTypeId) {
          tabs.selectTab(id);
        }
      });
    };
    this.nav.classList.add("aras-nav-toc");
    this.nav.formatter = navFormatter.bind(null, this.nav);
    const handler = async (itemKey, event) => {
      if (
        event.type === "keydown" &&
        !["Enter", "Space", "NumpadEnter"].includes(event.code)
      ) {
        return;
      }
      const dataItem = this.nav.data.get(itemKey);
      const isNewTabOpened =
        dataItem.on_click_handler ||
        dataItem.formId ||
        dataItem.startPage ||
        event.target.closest(".aras-button.aras-nav-leaf-ico");
      if (isNewTabOpened) {
        this._hideIfNotPinned();
      } else if (dataItem.itemTypeId) {
        await this._showSecondaryMenu(dataItem.itemTypeId);
        selectTab(dataItem);
      }
    };
    this.nav.on("click", handler);
    this.nav.on("keydown", handler);
  }

  _initTabs() {
    this.tabs.on("select", (id) => {
      if (id === contentTabId) {
        this._hideSecondaryMenu();
        return;
      }

      const item = this.tabs.data.get(id);
      this._showSecondaryMenu(item.itemTypeId);
    });
    this.tabs.on("click", (id) => {
      if (id === contentTabId) {
        this._hideSecondaryMenu();
      }
    });
  }

  _isElementInSidebar(element) {
    return (
      super._isElementInSidebar(element) ||
      (element && this.popupMenu.dom.contains(element))
    );
  }

  _hideIfNotPinned() {
    if (!this.isPinned) {
      this.toggleVisibility(false);
      document.documentElement.focus(); // for correct work of sidebar in IE and FF
    }
  }

  _getSecondaryMenuData() {
    const itemTypeId = this._secondaryMenuItemTypeId;
    if (!itemTypeId) {
      return null;
    }

    const itemType = aras.getItemTypeDictionary(itemTypeId, "id");
    if (itemType.isError()) {
      return null;
    }

    const propertiesWithKeyedNameOrderCount = itemType
      .getItemsByXPath(
        'Relationships/Item[@type="Property" and keyed_name_order[not(@is_null)]]'
      )
      .getItemCount();
    const itemTypeName = itemType.getProperty("name");
    const singularLabel = itemType.getProperty("label") || itemTypeName;
    const isQuickSearchDisabled =
      window.isFunctionDisabled(itemTypeName, "Open") ||
      propertiesWithKeyedNameOrderCount === 0;
    const pluralLabel = itemType.getProperty("label_plural") || singularLabel;
    const canAdd = aras.getPermissions("can_add", itemTypeId);
    const icon = itemType.getProperty("open_icon") || icons.defaultItemType;
    const favoritesFilterCriteria = (favoriteItem) => {
      return (
        favoriteItem.quickAccessFlag === "1" &&
        favoriteItem.contextType === itemTypeName
      );
    };
    const favoriteSearches = window.favorites.getDataMap(
      "Search",
      favoritesFilterCriteria
    );
    const favoriteItems = window.favorites.getDataMap(
      "Item",
      favoritesFilterCriteria
    );
    const favoriteCategories = [];
    if (favoriteSearches.size) {
      favoriteCategories.push({
        icon,
        overlayIcons: [icons.savedSearch],
        unpinIcon: icons.close,
        label: aras.getResource(
          "",
          "navigation_panel.secondary_menu.favorite_searches"
        ),
        items: Array.from(favoriteSearches.values()),
      });
    }
    if (favoriteItems.size) {
      favoriteCategories.push({
        icon,
        label: pluralLabel,
        items: Array.from(favoriteItems.values()),
      });
    }
    const loggedUserIdentity = aras.getIsAliasIdentityIDForLoggedUser();

    return {
      icon,
      singularLabel,
      pluralLabel,
      itemTypeId,
      canAdd,
      loggedUserIdentity,
      favoriteCategories,
      itemTypeName,
      isQuickSearchDisabled,
    };
  }
  
  async _showSecondaryMenu(itemTypeId) {
    this._secondaryMenuItemTypeId = itemTypeId;
    await this.render();
    this.nav.style.visibility = "hidden";
    this.focus();
    this._clearSearch();
  }

  _hideSecondaryMenu() {
    this._secondaryMenuItemTypeId = null;
    this.tabs.selectTab(contentTabId);
    this.render();
    this.nav.style.visibility = "visible";
    this.nav.focus();
  }

  _favoritesClick(event) {
    const favoriteItemRow = event.target.closest(".aras-nav__child");
    if (!favoriteItemRow) {
      return;
    }

    const favoriteId = favoriteItemRow.dataset.id;
    const favoriteItem = window.favorites.get(favoriteId);
    const unpinIconNode = event.target.closest(
      ".aras-button.aras-nav-leaf-ico"
    );
    if (unpinIconNode) {
      window.favorites.removeFromQuickAccess(favoriteId);
    } else if (favoriteItem.category === "Item") {
      aras.uiShowItemLastVersion(
        favoriteItem.contextType,
        favoriteItem.additional_data.id
      );
      this._hideIfNotPinned();
    } else if (favoriteItem.category === "Search") {
      const secondaryMenuData = this._getSecondaryMenuData();
      this._searchItemType(secondaryMenuData.itemTypeId, favoriteId);
    }
  }

  _createNewItem(itemTypeId) {
    return aras.uiNewItemExAsync(itemTypeId).then(() => {
      this._hideIfNotPinned();
    });
  }

  _searchItemType(itemTypeId, favoriteSearchId) {
    return Promise.resolve(
      window.arasTabs.openSearch(itemTypeId, favoriteSearchId)
    ).then(() => {
      this._hideIfNotPinned();
    });
  }

  async _openItem(event) {
    const quickSearch = event.currentTarget;
    const selectedItem = quickSearch.getSelectedItem();
    if (!selectedItem) {
      return;
    }
    quickSearch.clear();
    await aras.uiShowItem(quickSearch.state.itemType, selectedItem.itemId);
  }
}

NavigationPanel.define("aras-navigation-panel");
